


import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DECKS } from '../data/stimuli';
import { CARD_IMAGES } from '../data/cardImages';
import { useAuth } from '../hooks/useAuth';
import {
  saveVariationSelections,
  getVariationSelections,
  saveSessionResult,
  getRecentSessionResults,
  getSessionLogs,
  getDeckAdditions,
  getCustomDeckForSession,
  getCustomDeckById,
} from '../services/firestoreService';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { getProfiles, saveProfileSession } from '../services/profileService';
import { loadSettings } from '../hooks/useApplySettings';
import { useTranslation } from '../i18n/useTranslation';
import logoWordmark from '../assets/logo.png';
import '../styles/session.css';

// ── Audio helpers ────────────────────────────────────────────────────────────
function playBeep(correct) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = correct ? 660 : 220;
    osc.type = correct ? 'sine' : 'sawtooth';
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (correct ? 0.45 : 0.3));
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  } catch {}
}

function speakLabel(label, lang) {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(label);
    utt.rate = 0.95;
    if (lang) utt.lang = lang;
    window.speechSynthesis.speak(utt);
  } catch {}
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function buildTrial(deck, targetItems, settings) {
  if (!targetItems?.length) return null;

  const target = targetItems[Math.floor(Math.random() * targetItems.length)];

  // Pick a random variation image (or the base image) for within-stimulus generalization
  const allImages = [target.image, ...(target.variations || [])].filter(Boolean);
  const pickedImage = allImages.length > 0
    ? allImages[Math.floor(Math.random() * allImages.length)]
    : null;
  const targetWithVariation = { ...target, image: pickedImage };

  if (settings.distractorMode === 'none') {
    return { target, choices: [targetWithVariation] };
  }

  const enabledSet = settings.enabledDistractors ? new Set(settings.enabledDistractors) : null;
  const distractorSource = settings.distractorSource || 'same';

  let distractorPool = [];

  // Same-deck pool: use deckItems from settings so custom-added cards are included.
  // Falls back to deck.items if deckItems was not passed (e.g. built-in decks with no additions).
  if (distractorSource === 'same' || distractorSource === 'mixed') {
    const pool = settings.deckItems || deck.items;
    const samePool = pool.filter(item =>
      item.label !== target.label &&
      (!enabledSet || enabledSet.has(item.label))
    );
    distractorPool = [...distractorPool, ...samePool];
  }

  // Cross-deck pool: cards from the selected other decks, never the current target
  if ((distractorSource === 'cross' || distractorSource === 'mixed') &&
      settings.rotationDecks?.length > 0) {
    const crossPool = DECKS
      .filter(d => settings.rotationDecks.includes(d.name))
      .flatMap(d => d.items.filter(i => i.label !== target.label));
    distractorPool = [...distractorPool, ...crossPool];
  }

  // Deduplicate by label — mixed mode can produce the same label from multiple decks
  const seenLabels = new Set([target.label]);
  const uniquePool = distractorPool.filter(item => {
    if (seenLabels.has(item.label)) return false;
    seenLabels.add(item.label);
    return true;
  });

  // Fisher-Yates shuffle for uniform distribution
  for (let i = uniquePool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [uniquePool[i], uniquePool[j]] = [uniquePool[j], uniquePool[i]];
  }

  const numChoices = settings.numChoices ?? 4;
  // Give every distractor a random variation image too (same logic as target)
  const distractors = uniquePool.slice(0, numChoices - 1).map(item => {
    const imgs = [item.image, ...(item.variations || [])].filter(Boolean);
    const picked = imgs.length > 0 ? imgs[Math.floor(Math.random() * imgs.length)] : null;
    return { ...item, image: picked };
  });
  const choices = [targetWithVariation, ...distractors];

  // Shuffle choices with Fisher-Yates
  for (let i = choices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [choices[i], choices[j]] = [choices[j], choices[i]];
  }

  return { target, choices };
}

const getChoiceCols = (n) => {
  if (n <= 2) return 2;
  if (n <= 3) return 3;
  if (n <= 4) return 2;
  if (n <= 6) return 3;
  if (n <= 8) return 4;
  return 5;
};

// Try imageBackup before hiding on error
const imgOnError = (e) => {
  const t = e.currentTarget;
  const b = t.dataset.backup;
  if (b && t.src !== b) { t.src = b; } else { t.style.display = 'none'; }
};

// ── Quit Confirmation Modal ───────────────────────────────────────────────────

const QuitModal = ({ onConfirm, onCancel }) => (
  <div className="quit-overlay">
    <div className="quit-modal">
      <h2 className="quit-title">Quit this session?</h2>
      <p className="quit-body">Data from this session will not be saved.</p>
      <div className="quit-actions">
        <button className="quit-cancel-btn" onClick={onCancel}>Keep Going</button>
        <button className="quit-confirm-btn" onClick={onConfirm}>Quit Session</button>
      </div>
    </div>
  </div>
);

// ── Sub-components ────────────────────────────────────────────────────────────

const SessionHeaderBar = ({ onBack, title }) => (
  <div className="session-header-bar">
    <button className="sc-back-link" onClick={onBack}>← Back to Decks</button>
    {title && <span className="session-header-title">{title}</span>}
    <div style={{ width: 120 }} />
  </div>
);

const TogglePills = ({ options, value, onChange }) => (
  <div className="toggle-pills">
    {options.map((opt) => (
      <button
        key={opt.value}
        className={`toggle-pill ${value === opt.value ? 'active' : ''}`}
        onClick={() => onChange(opt.value)}
        type="button"
      >
        {opt.label}
      </button>
    ))}
  </div>
);

function timeSince(date) {
  if (!date) return null;
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins === 1) return '1 minute ago';
  return `${mins} minutes ago`;
}

function formatDate(raw) {
  if (!raw) return 'Unknown date';
  const d = raw?.toDate ? raw.toDate() : raw?.seconds ? new Date(raw.seconds * 1000) : new Date(raw);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

const PEXELS_KEY = process.env.REACT_APP_PEXELS_API_KEY;

const VariationsModal = ({ item, deckName, onClose, onDone, initialActiveIds }) => {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;

  const baseVariations = CARD_IMAGES[item?.label]?.variations || [];

  const [activeIds,        setActiveIds]        = useState(() =>
    initialActiveIds ? new Set(initialActiveIds) : new Set(baseVariations.map(v => v.id))
  );
  const [removedIds,       setRemovedIds]       = useState(new Set());
  const [customVariations, setCustomVariations] = useState([]);
  const [photoOverrides,   setPhotoOverrides]   = useState({});
  const [saving,           setSaving]           = useState(false);
  const [savedOk,          setSavedOk]          = useState(false);

  // Add-photo panel state
  const [showAdd,      setShowAdd]      = useState(false);
  const [query,        setQuery]        = useState('');
  const [results,      setResults]      = useState([]);
  const [searching,    setSearching]    = useState(false);
  const [searchErr,    setSearchErr]    = useState('');
  const addInputRef = useRef(null);

  // Load saved selections from Firestore on open
  useEffect(() => {
    if (!item || !uid) return;
    let mounted = true;
    const base = CARD_IMAGES[item.label]?.variations || [];
    setActiveIds(initialActiveIds ? new Set(initialActiveIds) : new Set(base.map(v => v.id)));
    setRemovedIds(new Set());
    setCustomVariations([]);
    setPhotoOverrides({});
    setSavedOk(false);
    setShowAdd(false);
    setResults([]);

    getVariationSelections(uid, deckName, item.label).then((res) => {
      if (!mounted || !res.success || !res.data) return;
      const d = res.data;
      if (d.selectedIndexes) {
        const idxSet = new Set(d.selectedIndexes);
        const savedIds = base.filter((_, i) => idxSet.has(i)).map(v => v.id);
        if (savedIds.length > 0) setActiveIds(new Set(savedIds));
      }
      if (d.removedIds?.length)    setRemovedIds(new Set(d.removedIds));
      if (d.customVariations?.length) {
        setCustomVariations(d.customVariations);
        // custom variations are always active unless they were previously deactivated
        setActiveIds(prev => {
          const next = new Set(prev);
          d.customVariations.forEach(v => next.add(v.id));
          return next;
        });
      }
    });
    return () => { mounted = false; };
  }, [item?.label, uid, deckName]);

  useEffect(() => {
    if (showAdd) setTimeout(() => addInputRef.current?.focus(), 80);
  }, [showAdd]);

  if (!item) return null;

  // Visible built-in variations (not permanently removed)
  const visibleBase = baseVariations.filter(v => !removedIds.has(v.id));

  const toggleActive = (id) => {
    setActiveIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const removeBuiltin = (e, id) => {
    e.stopPropagation();
    setRemovedIds(prev => new Set([...prev, id]));
    setActiveIds(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const removeCustom = (e, id) => {
    e.stopPropagation();
    setCustomVariations(prev => prev.filter(v => v.id !== id));
    setActiveIds(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const togglePhoto = (e, variation) => {
    e.stopPropagation();
    if (!variation.backup) return;
    setPhotoOverrides(prev => {
      const cur = prev[variation.id] || variation.image;
      return { ...prev, [variation.id]: cur === variation.image ? variation.backup : variation.image };
    });
  };

  const searchPexels = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setSearchErr('');
    try {
      const res = await fetch(
        `https://api.pexels.com/v1/search?query=${encodeURIComponent(q)}&per_page=15&orientation=square`,
        { headers: { Authorization: PEXELS_KEY } }
      );
      const data = await res.json();
      setResults(data.photos || []);
      if (!data.photos?.length) setSearchErr('No photos found — try different words.');
    } catch {
      setSearchErr('Search failed. Check your connection and try again.');
    }
    setSearching(false);
  };

  const addCustom = (photo) => {
    const id = `custom_${photo.id}`;
    if (customVariations.find(v => v.id === id)) return;
    const image = photo.src.medium;
    const newVar = { id, label: item.label, image };
    setCustomVariations(prev => [...prev, newVar]);
    setActiveIds(prev => new Set([...prev, id]));
    setResults([]);
    setQuery('');
    setShowAdd(false);
  };

  const varImgError = (e, backup) => {
    if (backup && e.currentTarget.src !== backup) e.currentTarget.src = backup;
    else e.currentTarget.closest?.('.var-card-img-wrap')?.classList.add('var-img-missing');
  };

  const handleSave = async () => {
    if (!uid || saving) return;
    setSaving(true);
    const activeIndexes = baseVariations
      .map((v, i) => ({ v, i }))
      .filter(({ v }) => !removedIds.has(v.id) && activeIds.has(v.id))
      .map(({ i }) => i);
    const res = await saveVariationSelections(
      uid, deckName, item.label,
      activeIndexes,
      [...removedIds],
      customVariations,
    );
    setSaving(false);
    if (res.success) { setSavedOk(true); setTimeout(() => setSavedOk(false), 2000); }
  };

  const handleDone = async () => {
    await handleSave();
    onDone?.(item.label, [...activeIds]);
    onClose();
  };

  return (
    <div className="variations-overlay" onClick={onClose}>
      <div className="var-modal-redesign" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="var-header">
          <div className="var-header-left">
            <span className="var-header-emoji">{item.emoji}</span>
            <div>
              <h3 className="var-header-title">{item.label}</h3>
              <p className="var-header-sub">Variations</p>
            </div>
          </div>
          <button className="var-close-btn" onClick={onClose} type="button">✕</button>
        </div>

        <hr className="var-divider" />

        <p className="var-hint">
          Tap a card to enable/disable it. Hit <strong>×</strong> to permanently remove a photo,
          or <strong>+ Add Photo</strong> to search for a better one.
        </p>

        {/* Variation grid — built-in */}
        <div className="var-grid">
          {visibleBase.map(variation => {
            const isActive = activeIds.has(variation.id);
            return (
              <div
                key={variation.id}
                className={`var-card${isActive ? ' var-card-on' : ' var-card-off'}`}
                onClick={() => toggleActive(variation.id)}
              >
                <div className="var-card-img-wrap">
                  <img
                    src={photoOverrides[variation.id] || variation.image}
                    alt={variation.label}
                    className="var-card-img"
                    onError={e => varImgError(e, variation.backup)}
                  />
                  <span className="var-card-emoji-fallback">{item.emoji}</span>
                  {isActive && <div className="var-card-check">✓</div>}
                  {!isActive && <div className="var-card-dim" />}
                  {variation.backup && (
                    <button className="var-change-photo-btn" onClick={e => togglePhoto(e, variation)} type="button" title="Switch photo">↻</button>
                  )}
                  <button className="var-remove-btn" onClick={e => removeBuiltin(e, variation.id)} type="button" title="Remove this photo">×</button>
                </div>
                <div className="var-card-label">{variation.label}</div>
              </div>
            );
          })}

          {/* Custom variations */}
          {customVariations.map(variation => (
            <div
              key={variation.id}
              className="var-card var-card-on var-card-custom"
              onClick={() => toggleActive(variation.id)}
            >
              <div className="var-card-img-wrap">
                <img src={variation.image} alt={variation.label} className="var-card-img" onError={e => { e.currentTarget.style.display='none'; }} />
                <span className="var-card-emoji-fallback">{item.emoji}</span>
                <div className="var-card-check">✓</div>
                <div className="var-custom-badge">Custom</div>
                <button className="var-remove-btn" onClick={e => removeCustom(e, variation.id)} type="button" title="Delete this photo">×</button>
              </div>
              <div className="var-card-label">{variation.label}</div>
            </div>
          ))}
        </div>

        {/* Add Photo panel */}
        <div className="var-add-section">
          {!showAdd ? (
            <button className="var-add-trigger" onClick={() => setShowAdd(true)} type="button">
              + Add Photo
            </button>
          ) : (
            <div className="var-add-panel">
              <form className="var-search-row" onSubmit={searchPexels}>
                <input
                  ref={addInputRef}
                  className="var-search-input"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={`Search photos for "${item.label}"…`}
                />
                <button className="var-search-btn" type="submit" disabled={searching}>
                  {searching ? '…' : 'Search'}
                </button>
                <button className="var-search-cancel" type="button" onClick={() => { setShowAdd(false); setResults([]); setQuery(''); }}>✕</button>
              </form>
              {searchErr && <p className="var-search-err">{searchErr}</p>}
              {results.length > 0 && (
                <div className="var-search-results">
                  {results.map(photo => (
                    <button
                      key={photo.id}
                      className="var-search-thumb"
                      type="button"
                      onClick={() => addCustom(photo)}
                      title="Add this photo"
                    >
                      <img src={photo.src.small} alt={photo.alt || item.label} />
                      <span className="var-search-thumb-plus">+</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <hr className="var-divider" style={{ marginTop: 12 }} />
        <div className="var-footer">
          <button className="var-save-btn" onClick={handleSave} disabled={saving} type="button">
            {savedOk ? '✓ Saved' : saving ? 'Saving…' : 'Save'}
          </button>
          <button className="var-done-btn" onClick={handleDone} disabled={saving} type="button">
            ✓ Done
          </button>
        </div>

      </div>
    </div>
  );
};

// ── Setup Phase ───────────────────────────────────────────────────────────────

const getRecentDecks = () => {
  try { return JSON.parse(localStorage.getItem('sc-recent-decks') || '[]'); } catch { return []; }
};
const recordRecentDeck = (deckName) => {
  const recent = getRecentDecks().filter(n => n !== deckName);
  localStorage.setItem('sc-recent-decks', JSON.stringify([deckName, ...recent].slice(0, 10)));
};

const SetupPhase = ({ deck, onStart, onBack }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { currentUser } = useAuth();
  const { tc } = useTranslation();
  const [profiles, setProfiles] = useState([]);
  const [activeProfileId, setActiveProfileId] = useState(
    () => localStorage.getItem('sc-active-profile-id') ?? ''
  );
  const [showProfileDrop, setShowProfileDrop] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    getProfiles(currentUser.uid).then((res) => {
      if (res.success) setProfiles(res.data);
    });
  }, [currentUser]);

  const activeProfile = profiles.find(p => p.id === activeProfileId) ?? null;

  const switchProfile = (id) => {
    setActiveProfileId(id);
    if (id) localStorage.setItem('sc-active-profile-id', id);
    else localStorage.removeItem('sc-active-profile-id');
    setShowProfileDrop(false);
  };

  const [displayMode, setDisplayMode] = useState('image-text');
  const [distractorMode, setDistractorMode]   = useState('add');
  const [trialCount, setTrialCount] = useState(5);
  const [numChoices, setNumChoices] = useState(4);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [search, setSearch] = useState('');
  const [variationsItem, setVariationsItem] = useState(null);
  const [maxExceeded, setMaxExceeded] = useState(false);
  const [pendingNumChoices, setPendingNumChoices] = useState(null);
  const [distractorSource, setDistractorSource] = useState('same');
  const [rotationDecks, setRotationDecks] = useState([]);
  const [showDeckSearch, setShowDeckSearch] = useState(false);
  const [deckSearchQuery, setDeckSearchQuery] = useState('');
  const [customCards, setCustomCards] = useState([]);
  const [enabledDistractors, setEnabledDistractors] = useState(
    () => new Set((deck?.items || []).map(i => i.label))
  );

  const [variationCache, setVariationCache] = useState({});

  useEffect(() => {
    // Custom decks already contain all their cards in deck.items — skip additions
    if (!currentUser || deck.isCustom) return;
    getDeckAdditions(currentUser.uid, deck.name).then(res => {
      if (res.success && res.data.length > 0) {
        setCustomCards(res.data);
        setEnabledDistractors(prev => {
          const next = new Set(prev);
          res.data.forEach(c => { if (c.label) next.add(c.label); });
          return next;
        });
      }
    });
  }, [currentUser, deck.name, deck.isCustom]);

  const allItems = [...deck.items, ...customCards];

  // Auto-select a card when navigated here after adding one
  useEffect(() => {
    const label = location.state?.autoSelect;
    if (!label) return;
    const exists = allItems.find(i => i.label === label);
    if (exists) setSelectedTargets([label]);
    // Clear state so re-renders don't re-trigger
    window.history.replaceState({}, '');
  }, [allItems, location.state?.autoSelect]);

  const handleVariationsDone = (label, activeIds) => {
    setVariationCache(prev => ({ ...prev, [label]: activeIds }));
  };

  const getCardImage = (item) => {
    const cached = variationCache[item.label];
    if (cached && cached.length > 0) {
      const vars = CARD_IMAGES[item.label]?.variations || [];
      const activeVar = vars.find(v => cached.includes(v.id));
      if (activeVar) return activeVar.image;
    }
    return item.image;
  };

  const lastTap = useRef({});

  const withDistractors = distractorMode !== 'none';
  const maxTargets = withDistractors ? numChoices : allItems.length;
  const atMax = withDistractors && selectedTargets.length >= maxTargets;

  const filtered = allItems.filter((item) =>
    item.label.toLowerCase().includes(search.toLowerCase())
  );

  const toggleTarget = (item) => {
    if (selectedTargets.includes(item.label)) {
      setSelectedTargets((prev) => prev.filter((l) => l !== item.label));
      setMaxExceeded(false);
    } else if (atMax) {
      setMaxExceeded(true);
    } else {
      setSelectedTargets((prev) => [...prev, item.label]);
      setMaxExceeded(false);
    }
  };

  const handleNumChoicesChange = (newVal) => {
    const v = Math.min(10, Math.max(2, newVal || 2));
    if (withDistractors && selectedTargets.length > v) {
      setPendingNumChoices(v);
    } else {
      setNumChoices(v);
    }
  };

  const confirmReduceChoices = () => {
    setSelectedTargets((prev) => prev.slice(0, pendingNumChoices));
    setNumChoices(pendingNumChoices);
    setMaxExceeded(false);
    setPendingNumChoices(null);
  };

  const toggleDistractor = (label) => {
    setEnabledDistractors(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };

  const handleCardTap = (item) => {
    const now = Date.now();
    const last = lastTap.current[item.label] || 0;
    if (now - last < 350) {
      setVariationsItem(item);
    } else {
      toggleTarget(item);
    }
    lastTap.current[item.label] = now;
  };

  const canStart = selectedTargets.length > 0;
  const targetItems = allItems.filter((i) => selectedTargets.includes(i.label));

  // Prevent starting when cross/mixed mode has no distractor decks selected.
  const crossDeckGuardFailed =
    withDistractors &&
    (distractorSource === 'cross' || distractorSource === 'mixed') &&
    rotationDecks.length === 0;

  const handleStart = () => {
    if (!canStart) return;
    recordRecentDeck(deck.name);
    onStart({
      settings: { displayMode, distractorMode, distractorSource, numChoices, rotationDecks: withDistractors ? rotationDecks : [], enabledDistractors: Array.from(enabledDistractors), deckItems: allItems },
      trialCount,
      targetItems,
    });
  };

  const [wizardStep, setWizardStep] = useState(1);

  const STEPS = [
    { num: 1, label: 'Targets' },
    { num: 2, label: 'Distractors' },
    { num: 3, label: 'Review' },
  ];

  const hintText = {
    1: "You will choose distractor settings next. We'll show you a preview on the last step!",
    2: 'Almost there! Review your settings and start the session on the next step.',
    3: 'Everything look good? Hit Start Session to begin!',
  };

  const distractorDeckPicker = (() => {
    const otherDecks = DECKS.filter(d => d.name !== deck.name);
    const recentNames = getRecentDecks().filter(n => n !== deck.name);
    const suggested = [
      ...recentNames.filter(n => otherDecks.find(d => d.name === n)),
      ...otherDecks.map(d => d.name).filter(n => !recentNames.includes(n)),
    ].slice(0, 4).map(n => otherDecks.find(d => d.name === n)).filter(Boolean);
    const remaining = otherDecks.filter(d => !suggested.find(s => s.name === d.name));
    const filteredRemaining = remaining.filter(d =>
      d.name.toLowerCase().includes(deckSearchQuery.toLowerCase())
    );
    return { suggested, remaining, filteredRemaining };
  })();

  return (
    <div className="wz-page">
      <SessionHeaderBar onBack={onBack} />

      <div className="wz-layout">
        {/* ── Left stepper ── */}
        <div className="wz-stepper">
          {STEPS.map(({ num, label }) => (
            <div key={num} className="wz-step-item">
              <div className="wz-step-track">
                <div className={`wz-step-circle ${wizardStep === num ? 'wz-active' : wizardStep > num ? 'wz-done' : 'wz-pending'}`}>
                  {wizardStep > num ? '✓' : num}
                </div>
                {num < 3 && <div className={`wz-step-line ${wizardStep > num ? 'wz-line-done' : ''}`} />}
              </div>
              <span className={`wz-step-label ${wizardStep === num ? 'wz-label-active' : ''}`}>{label}</span>
            </div>
          ))}
        </div>

        {/* ── Main wizard card ── */}
        <div className="wz-main">
          <div className="wz-card">

            {/* ── Step 1: Targets ── */}
            {wizardStep === 1 && (
              <>
                <h2 className="wz-step-title">Step 1: Choose Targets</h2>

                {withDistractors && (
                  <div className="wz-field">
                    <label className="wz-field-label">Number of Choices per Trial</label>
                    <div className="choices-stepper">
                      <button className="choices-step-btn" type="button"
                        disabled={numChoices <= 2}
                        onClick={() => handleNumChoicesChange(numChoices - 1)}>−</button>
                      <input className="choices-input" type="number" min={2} max={10}
                        value={numChoices}
                        onChange={(e) => handleNumChoicesChange(Number(e.target.value))} />
                      <button className="choices-step-btn" type="button"
                        disabled={numChoices >= 10}
                        onClick={() => handleNumChoicesChange(numChoices + 1)}>+</button>
                    </div>
                    <p className="wz-field-hint" style={{ marginTop: 6 }}>
                      Total cards shown per trial (target + distractors). Also limits how many targets you can select.
                    </p>
                    {pendingNumChoices !== null && (
                      <div className="choices-reduce-warning" style={{ marginTop: 10 }}>
                        <p className="choices-reduce-text">
                          Reducing to {pendingNumChoices} will deselect some targets. Continue?
                        </p>
                        <div className="choices-reduce-btns">
                          <button className="choices-reduce-confirm" type="button" onClick={confirmReduceChoices}>Confirm</button>
                          <button className="choices-reduce-cancel" type="button" onClick={() => setPendingNumChoices(null)}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="wz-deck-display">
                  <span className="wz-deck-emoji">{deck.emoji}</span>
                  <span className="wz-deck-name-lg">{tc(deck.name)}</span>
                </div>

                <div className="wz-field">
                  <div className="wz-field-row">
                    <label className="wz-field-label">Select Target Cards</label>
                    <span className="wz-count-badge">
                      {withDistractors
                        ? `${selectedTargets.length} / ${maxTargets}`
                        : `${selectedTargets.length} selected`}
                    </span>
                  </div>
                  <p className="wz-field-hint">Choose the cards you want to teach.</p>
                  <input
                    className="wz-search"
                    type="text"
                    placeholder="Search cards…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {maxExceeded && (
                    <p className="wz-max-msg">
                      Max {maxTargets} card{maxTargets !== 1 ? 's' : ''} for this session
                    </p>
                  )}
                  <div className="wz-card-grid">
                    {filtered.map((item) => {
                      const isSelected = selectedTargets.includes(item.label);
                      const isLocked = atMax && !isSelected;
                      return (
                        <div
                          key={item.label}
                          className={`wz-card-item${isSelected ? ' wz-selected' : ''}${isLocked ? ' wz-locked' : ''}`}
                          onClick={() => handleCardTap(item)}
                        >
                          <div className="wz-card-face" style={{ backgroundColor: deck.color }}>
                            <span className="wz-card-emoji">{item.emoji}</span>
                            {item.image && (
                              <img
                                src={getCardImage(item)}
                                alt={tc(item.label)}
                                className="wz-card-img"
                                data-backup={item.imageBackup}
                                onError={imgOnError}
                              />
                            )}
                            {isLocked && <div className="wz-card-lock-overlay">Max</div>}
                          </div>
                          <span className="wz-card-label">{tc(item.label)}</span>
                          <div className={`wz-checkbox${isSelected ? ' wz-checked' : ''}`}>
                            {isSelected && <span className="wz-checkmark">✓</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    className="wz-add-card-btn"
                    type="button"
                    onClick={() => navigate(`/deck/${encodeURIComponent(deck.name)}/add-card`)}
                  >
                    + Add a card to this deck
                  </button>
                </div>
              </>
            )}

            {/* ── Step 2: Distractors ── */}
            {wizardStep === 2 && (
              <>
                <h2 className="wz-step-title">Step 2: Distractors</h2>

                <div className="wz-field">
                  <label className="wz-field-label">Distractor Mode</label>
                  <TogglePills
                    value={distractorMode}
                    onChange={setDistractorMode}
                    options={[
                      { value: 'none', label: 'No Distractors' },
                      { value: 'add',  label: 'Add Distractors' },
                    ]}
                  />
                </div>

                {withDistractors && (
                  <>
                    <div className="wz-field">
                      <label className="wz-field-label">Distractor Source</label>
                      <TogglePills
                        value={distractorSource}
                        onChange={(val) => {
                          setDistractorSource(val);
                          if (val === 'same') { setRotationDecks([]); setShowDeckSearch(false); }
                        }}
                        options={[
                          { value: 'same',  label: 'Same Deck' },
                          { value: 'cross', label: 'Other Deck(s)' },
                          { value: 'mixed', label: 'Mixed' },
                        ]}
                      />
                      <p className="wz-field-hint" style={{ marginTop: 8 }}>
                        {distractorSource === 'same'  && 'Distractors come from the same category as the target.'}
                        {distractorSource === 'cross' && 'Distractors come from a different category — select decks below.'}
                        {distractorSource === 'mixed' && "Distractors come from both the target's deck and selected decks below."}
                      </p>
                    </div>

                    {(distractorSource === 'cross' || distractorSource === 'mixed') && (
                      <div className="wz-field">
                        <label className="wz-field-label">Distractor Deck(s)</label>
                        <div className="deck-chips-row">
                          {distractorDeckPicker.suggested.map(d => {
                            const isSelected = rotationDecks.includes(d.name);
                            return (
                              <button
                                key={d.name}
                                className={`deck-chip${isSelected ? ' selected' : ''}`}
                                style={{ '--chip-color': d.color }}
                                onClick={() =>
                                  setRotationDecks(prev =>
                                    isSelected ? prev.filter(n => n !== d.name) : [...prev, d.name]
                                  )
                                }
                              >
                                <span>{d.emoji}</span>
                                <span>{d.name}</span>
                                {isSelected && <span className="deck-chip-check">✓</span>}
                              </button>
                            );
                          })}
                          {distractorDeckPicker.remaining.length > 0 && (
                            <button
                              className={`deck-chip deck-chip-more${showDeckSearch ? ' active' : ''}`}
                              onClick={() => { setShowDeckSearch(p => !p); setDeckSearchQuery(''); }}
                            >
                              +
                            </button>
                          )}
                        </div>
                        {rotationDecks.length === 0 && (
                          <p className="deck-picker-empty-hint">Select at least one deck</p>
                        )}
                        {showDeckSearch && (
                          <div className="deck-search-panel">
                            <input
                              className="deck-search-input"
                              type="text"
                              placeholder="Search decks…"
                              value={deckSearchQuery}
                              onChange={e => setDeckSearchQuery(e.target.value)}
                              autoFocus
                            />
                            <div className="deck-search-results">
                              {distractorDeckPicker.filteredRemaining.map(d => {
                                const isSelected = rotationDecks.includes(d.name);
                                return (
                                  <button
                                    key={d.name}
                                    className={`deck-search-row${isSelected ? ' selected' : ''}`}
                                    onClick={() =>
                                      setRotationDecks(prev =>
                                        isSelected ? prev.filter(n => n !== d.name) : [...prev, d.name]
                                      )
                                    }
                                  >
                                    <span className="deck-search-emoji">{d.emoji}</span>
                                    <span className="deck-search-name">{d.name}</span>
                                    {isSelected && <span className="deck-search-check">✓</span>}
                                  </button>
                                );
                              })}
                              {distractorDeckPicker.filteredRemaining.length === 0 && (
                                <p className="deck-search-empty">No decks found</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                  </>
                )}
              </>
            )}

            {/* ── Step 3: Review ── */}
            {wizardStep === 3 && (
              <>
                <h2 className="wz-step-title">Step 3: Review</h2>

                {/* Profile */}
                {profiles.length > 0 && (
                  <div className="wz-field">
                    <label className="wz-field-label">Session Profile</label>
                    <div style={{ position: 'relative' }}>
                      <div
                        className="setup-profile-badge"
                        onClick={() => setShowProfileDrop(d => !d)}
                        role="button" tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && setShowProfileDrop(d => !d)}
                      >
                        {activeProfile ? (
                          <>
                            <span className="setup-profile-avatar" style={{ background: activeProfile.color }}>{activeProfile.icon}</span>
                            <span>{activeProfile.nickname}</span>
                          </>
                        ) : (
                          <>
                            <span className="setup-profile-avatar" style={{ background: 'var(--border)', color: 'var(--text-muted)' }}>—</span>
                            <span style={{ color: 'var(--text-secondary)' }}>No profile selected</span>
                          </>
                        )}
                        <span className="setup-profile-switch">Switch ▾</span>
                      </div>
                      {showProfileDrop && (
                        <div className="setup-profile-dropdown">
                          <div className={`setup-profile-dropdown-item${!activeProfileId ? ' active' : ''}`} onClick={() => switchProfile('')}>
                            <span style={{ marginRight: 8, color: 'var(--text-muted)' }}>—</span>No profile
                          </div>
                          {profiles.map(p => (
                            <div key={p.id} className={`setup-profile-dropdown-item${activeProfileId === p.id ? ' active' : ''}`} onClick={() => switchProfile(p.id)}>
                              <span className="setup-profile-avatar" style={{ background: p.color, marginRight: 8 }}>{p.icon}</span>
                              {p.nickname}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Display Mode */}
                <div className="wz-field">
                  <label className="wz-field-label">Display Mode</label>
                  <TogglePills
                    value={displayMode}
                    onChange={setDisplayMode}
                    options={[
                      { value: 'text',       label: 'Text Only' },
                      { value: 'image',      label: 'Image Only' },
                      { value: 'image-text', label: 'Image + Text' },
                    ]}
                  />
                </div>

                {/* Trial Count */}
                <div className="wz-field">
                  <label className="wz-field-label">Number of Trials</label>
                  <select className="trials-select" value={trialCount}
                    onChange={(e) => { const v = e.target.value; setTrialCount(v === 'unlimited' ? 'unlimited' : Number(v)); }}>
                    {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n} trial{n>1?'s':''}</option>)}
                    <option value="unlimited">Unlimited</option>
                  </select>
                </div>

                {/* Summary */}
                <div className="wz-summary">
                  <div className="wz-summary-row">
                    <span className="wz-summary-label">Targets</span>
                    <span className="wz-summary-value">{selectedTargets.join(', ') || '—'}</span>
                  </div>
                  <div className="wz-summary-row">
                    <span className="wz-summary-label">Distractors</span>
                    <span className="wz-summary-value">
                      {distractorMode === 'none' ? 'None' :
                        distractorSource === 'same'  ? 'Same deck' :
                        distractorSource === 'cross' ? `Other: ${rotationDecks.join(', ') || 'none selected'}` :
                        `Mixed (same + ${rotationDecks.join(', ') || 'none selected'})`}
                    </span>
                  </div>
                  <div className="wz-summary-row">
                    <span className="wz-summary-label">Choices per trial</span>
                    <span className="wz-summary-value">{withDistractors ? numChoices : '1 (no distractors)'}</span>
                  </div>
                </div>
              </>
            )}

          </div>
        </div>
      </div>

      {/* ── Fixed footer: hint + nav ── */}
      <div className="wz-footer">
        <div className="wz-footer-hint">
          <span className="wz-hint-text">
            {wizardStep === 2 && crossDeckGuardFailed
              ? 'Select at least one distractor deck to continue.'
              : hintText[wizardStep]}
          </span>
        </div>
        <div className="wz-footer-nav">
          {wizardStep > 1 && (
            <button className="wz-back-btn" type="button" onClick={() => setWizardStep(s => s - 1)}>
              ← Back
            </button>
          )}
          {wizardStep < 3 ? (
            <button
              className="wz-next-btn"
              type="button"
              disabled={(wizardStep === 1 && !canStart) || (wizardStep === 2 && crossDeckGuardFailed)}
              onClick={() => setWizardStep(s => s + 1)}
            >
              Next →
            </button>
          ) : (
            <button
              className="wz-next-btn"
              type="button"
              disabled={!canStart}
              onClick={handleStart}
            >
              Start Session →
            </button>
          )}
        </div>
      </div>

      <VariationsModal
        item={variationsItem}
        deckName={deck.name}
        onClose={() => setVariationsItem(null)}
        onDone={handleVariationsDone}
        initialActiveIds={variationsItem ? variationCache[variationsItem.label] : undefined}
      />
    </div>
  );
};

// ── Celebration overlay ───────────────────────────────────────────────────────

const CelebrationOverlay = ({ visible }) => {
  if (!visible) return null;
  return (
    <div className="trial-celebration-overlay" key={Date.now()}>
      <div className="trial-celebration-ring">
        <span className="trial-celebration-check">✓</span>
        {[...Array(8)].map((_, i) => (
          <div key={i} className={`trial-sparkle trial-sparkle-${i}`} />
        ))}
      </div>
    </div>
  );
};

const IncorrectFlash = ({ visible }) => {
  if (!visible) return null;
  return <div className="trial-incorrect-flash" key={Date.now()} />;
};

// ── Trial Phase ───────────────────────────────────────────────────────────────

const TrialPhase = ({ deck, trialCount, targetItems, settings, onComplete, onQuit }) => {
  const { tc, language } = useTranslation();
  const [currentTrial, setCurrentTrial] = useState(0);
  const [trialData, setTrialData] = useState(() => buildTrial(deck, targetItems, settings) ?? null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [results, setResults] = useState([]);
  const [showQuit, setShowQuit] = useState(false);

  // Speak target label when voice prompts is on
  useEffect(() => {
    if (!trialData?.target?.label) return;
    const s = loadSettings();
    if (s.voicePrompts) speakLabel(tc(trialData.target.label), language === 'es' ? 'es-ES' : undefined);
  }, [trialData, tc, language]);
  const [showResultsPanel, setShowResultsPanel] = useState(false);
  const [showLiveScore,    setShowLiveScore]    = useState(false);
  const [feedbackOn, setFeedbackOn] = useState(true);
  const [animationsOn, setAnimationsOn] = useState(true);
  const [celebrationKey, setCelebrationKey] = useState(0);
  const [incorrectKey, setIncorrectKey] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showIncorrectFlash, setShowIncorrectFlash] = useState(false);
  const [liveDisplayMode, setLiveDisplayMode] = useState(settings.displayMode || 'image-text');
  const activeTimers = useRef([]);
  // Mirrors `results` but updates synchronously (results only commits after the
  // 1.5s feedback-animation delay, by design, so the score display doesn't
  // jump ahead of the flash/celebration). "End session" needs the true latest
  // count immediately, or a trial answered just before clicking End is lost.
  const resultsRef = useRef([]);

  // Clean up all pending timers on unmount to prevent state updates after unmount
  useEffect(() => {
    return () => { activeTimers.current.forEach(clearTimeout); };
  }, []);

  const isUnlimited = trialCount === 'unlimited';
  const correct   = results.filter((r) => r.correct === true).length;
  const incorrect = results.filter((r) => r.correct === false).length;
  const progressPct = isUnlimited ? 0 : Math.round((currentTrial / trialCount) * 100);
  const cols = settings.distractorMode === 'none' ? 1 : getChoiceCols(settings.numChoices);

  const handleChoice = (index) => {
    if (selectedIndex !== null) return;

    const chosen = trialData.choices[index];
    if (!chosen) return;

    setSelectedIndex(index);

    const isCorrect =
      chosen.label === trialData.target.label ||
      (trialData.target.variations || []).includes(chosen.label);

    const newResults = [...results, { correct: isCorrect, label: trialData.target.label }];
    resultsRef.current = newResults;

    // Sound effects
    const appSettings = loadSettings();
    if (appSettings.soundEffects) playBeep(isCorrect);

    if (animationsOn) {
      if (isCorrect) {
        setCelebrationKey(k => k + 1);
        setShowCelebration(true);
        activeTimers.current.push(setTimeout(() => setShowCelebration(false), 900));
      } else {
        setIncorrectKey(k => k + 1);
        setShowIncorrectFlash(true);
        activeTimers.current.push(setTimeout(() => setShowIncorrectFlash(false), 600));
      }
    }

    activeTimers.current.push(setTimeout(() => {
      if (!isUnlimited && newResults.length >= trialCount) {
        onComplete(newResults);
      } else {
        const nextTrial = buildTrial(deck, targetItems, settings);
        if (!nextTrial) { onComplete(newResults); return; }
        setTrialData(nextTrial);
        setCurrentTrial((n) => n + 1);
        setSelectedIndex(null);
        setResults(newResults);
      }
    }, 1500));
  };

  const handleCardTap = (index) => {
    if (selectedIndex !== null) return;
    handleChoice(index);
  };

  const renderCardContent = (choice) => {
    const displayMode = liveDisplayMode;

    if (displayMode === 'text') {
      return (
        <div className="choice-text-face" style={{ backgroundColor: deck.color }}>
          <span className="choice-text-only-label">{tc(choice.label)}</span>
        </div>
      );
    }

    const face = (
      <div className="choice-card-face">
        <div className="choice-face-bg" style={{ backgroundColor: deck.color }}>
          <span className="choice-emoji-lg">{choice.emoji}</span>
        </div>
        {choice.image && (
          <img
            src={choice.image}
            alt={tc(choice.label)}
            className="choice-img-fill"
            data-backup={choice.imageBackup}
            onError={imgOnError}
          />
        )}
      </div>
    );

    if (displayMode === 'image') return face;

    return (
      <>
        {face}
        <div className="choice-label-strip">{tc(choice.label)}</div>
      </>
    );
  };

  if (!trialData) {
    return (
      <div className="session-page">
        <SessionHeaderBar onBack={onQuit} />
        <div className="session-body">
          <div className="setup-card" style={{ textAlign: 'center' }}>
            <p style={{ color: '#8A8A8A', marginBottom: 20 }}>No target items selected. Go back and select at least one target.</p>
            <button className="start-btn" onClick={onQuit}>← Go Back</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="session-page trial-phase">
      {showQuit && (
        <QuitModal onConfirm={onQuit} onCancel={() => setShowQuit(false)} />
      )}

      {/* Animation overlays */}
      {showCelebration && <CelebrationOverlay visible key={celebrationKey} />}
      {showIncorrectFlash && <IncorrectFlash visible key={incorrectKey} />}

      {/* Results side panel */}
      <div className={`trial-results-panel${showResultsPanel ? ' trial-results-panel--open' : ''}`}>
        <div className="trial-rp-header">
          <span className="trial-rp-title">Session Log</span>
          <button className="trial-rp-close" onClick={() => setShowResultsPanel(false)} type="button">×</button>
        </div>
        <div className="trial-rp-summary">
          <span className="trial-rp-sum-correct"><strong>{correct}</strong> Correct</span>
          <span className="trial-rp-sum-dot" />
          <span className="trial-rp-sum-wrong"><strong>{incorrect}</strong> Incorrect</span>
          {results.length > 0 && (
            <>
              <span className="trial-rp-sum-dot" />
              <span className="trial-rp-sum-acc"><strong>{Math.round((correct / results.length) * 100)}%</strong> Acc</span>
            </>
          )}
        </div>
        <div className="trial-rp-list">
          {results.length === 0 && (
            <p className="trial-rp-empty">No trials yet — results will appear here as you go.</p>
          )}
          {[...results].reverse().map((r, i) => (
            <div key={i} className={`trial-rp-row${r.correct ? ' trial-rp-row--correct' : ' trial-rp-row--wrong'}`}>
              <span className="trial-rp-num">#{results.length - i}</span>
              <span className="trial-rp-card-label">{r.label}</span>
              <span className="trial-rp-result">{r.correct ? '✓' : '✗'}</span>
            </div>
          ))}
        </div>
        {results.length > 0 && (
          <div className="trial-rp-footer">
            <span>{correct} of {results.length} correct</span>
            <span>Stored to your session history</span>
          </div>
        )}
      </div>

      {/* Controls row */}
      <div className="trial-controls-row">
        <button className="trial-quit-btn" onClick={() => setShowQuit(true)}>
          ← Quit
        </button>

        <div className="trial-center-group">
          <span className="trial-counter">
            {isUnlimited ? `Trial ${currentTrial + 1}` : `${currentTrial + 1} / ${trialCount}`}
          </span>
          {!isUnlimited && (
            <div className="trial-progress-mini">
              <div className="trial-progress-mini-fill" style={{ width: `${progressPct}%` }} />
            </div>
          )}
          {showLiveScore && results.length > 0 && (
            <span className="trial-score-pills">
              <span className="trial-score-correct">✓{correct}</span>
              <span className="trial-score-wrong">✗{incorrect}</span>
            </span>
          )}
        </div>

        <div className="trial-top-right">
          <button
            className="trial-icon-btn"
            onClick={() => setLiveDisplayMode(m =>
              m === 'image' ? 'text' : m === 'text' ? 'image-text' : 'image'
            )}
            type="button"
            title="Cycle display: Image → Text → Both"
          >
            {liveDisplayMode === 'image' ? 'Img' : liveDisplayMode === 'text' ? 'Txt' : 'Img+Txt'}
          </button>
          <button
            className={`trial-icon-btn${feedbackOn ? ' active' : ''}`}
            onClick={() => setFeedbackOn(f => !f)}
            type="button"
            title={feedbackOn ? 'Feedback on — tap to hide' : 'Feedback off — tap to show'}
          >
            {feedbackOn ? 'FB On' : 'FB Off'}
          </button>
          <button
            className={`trial-icon-btn${showLiveScore ? ' active' : ''}`}
            onClick={() => setShowLiveScore(s => !s)}
            type="button"
            title={showLiveScore ? 'Hide live score' : 'Show live score'}
          >
            Score
          </button>
          <button
            className={`trial-icon-btn${showResultsPanel ? ' active' : ''}`}
            onClick={() => setShowResultsPanel(p => !p)}
            type="button"
            title="Session log"
          >
            Log
          </button>
          {isUnlimited ? (
            <button
              className="end-session-btn"
              onClick={() => onComplete(resultsRef.current)}
              disabled={resultsRef.current.length === 0}
              title={resultsRef.current.length === 0 ? 'Complete at least one trial first' : 'End session'}
            >
              End
            </button>
          ) : null}
        </div>
      </div>

      {/* Prompt */}
      <div className="trial-prompt">
        Touch <span className="target-word">{trialData.target.label}</span>
      </div>

      {/* Choices card */}
      <div className={`trial-choices-card${showResultsPanel ? ' panel-open' : ''}`}>
        <div
          className="choices-grid"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 160px))` }}
        >
          {trialData.choices.map((choice, index) => {
            let cls = 'choice-card';
            if (selectedIndex !== null) {
              if (feedbackOn) {
                const isCorrectCard =
                  choice.label === trialData.target.label ||
                  (trialData.target.variations || []).includes(choice.label);
                if (isCorrectCard) cls += ' correct';
                else if (index === selectedIndex) {
                  cls += ' incorrect';
                  if (animationsOn) cls += ' shake';
                }
              } else if (index === selectedIndex) {
                cls += ' chosen';
              }
            }
            return (
              <div
                key={index}
                className={cls}
                onClick={() => handleCardTap(index)}
                onDoubleClick={(e) => { e.preventDefault(); }}
              >
                {renderCardContent(choice)}
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};

// ── Summary Phase ─────────────────────────────────────────────────────────────

const SummaryPhase = ({ results, deckName, deckEmoji, userId, onRunAgain, onBack, sessionData }) => {
  const { tc } = useTranslation();
  const correct   = results.filter(r => r.correct === true).length;
  const incorrect = results.filter(r => r.correct === false).length;
  const accuracy  = results.length > 0 ? Math.round((correct / results.length) * 100) : null;

  const [history,     setHistory]     = useState([]);
  const [sessionName, setSessionName] = useState(sessionData?.autoSavedName || '');
  const [saved,       setSaved]       = useState(sessionData?.autoSaved || false);
  const [saving,      setSaving]      = useState(false);

  // Auto-generate session name only if not already auto-saved
  useEffect(() => {
    if (!userId) return;
    let mounted = true;
    if (!sessionData?.autoSaved) {
      getSessionLogs(userId, 200).then(res => {
        if (!mounted) return;
        const n = res.success ? res.data.length + 1 : 1;
        setSessionName(`Session ${n}`);
      });
    }
    if (deckName) {
      getRecentSessionResults(userId, deckName, 5).then(res => {
        if (!mounted || !res.success) return;
        setHistory(res.data);
      });
    }
    return () => { mounted = false; };
  }, [userId, deckName, sessionData?.autoSaved]);

  // Compute commonly missed / correct from cardResults
  const cardResults = sessionData?.cardResults || [];
  const cardAgg = {};
  for (const r of cardResults) {
    if (!cardAgg[r.label]) cardAgg[r.label] = { correct: 0, wrong: 0 };
    if (r.correct) cardAgg[r.label].correct++;
    else cardAgg[r.label].wrong++;
  }
  const topMissed  = Object.entries(cardAgg).filter(([, v]) => v.wrong > 0)
    .sort((a, b) => b[1].wrong - a[1].wrong).slice(0, 4).map(([label, v]) => ({ label, ...v }));
  const topCorrect = Object.entries(cardAgg).filter(([, v]) => v.correct > 0)
    .sort((a, b) => b[1].correct - a[1].correct).slice(0, 4).map(([label, v]) => ({ label, ...v }));

  const handleSave = async () => {
    if (!userId || saved) return;
    setSaving(true);
    await saveSessionResult(userId, deckName, {
      ...sessionData,
      sessionName: sessionName.trim() || `Session`,
      deckEmoji: deckEmoji || '',
    });
    setSaved(true);
    setSaving(false);
  };

  return (
    <div className="session-page">
      <SessionHeaderBar onBack={onBack} />
      <div className="summary-scroll-body summary-scroll-body--padded">
        <div className="summary-content">

          {/* Accuracy card */}
          <div className="summary-accuracy-card">
            <div className="summary-check">✓</div>
            <h2 className="summary-title">Session Complete!</h2>
            {accuracy !== null && (
              <>
                <div className="summary-accuracy-pct">{accuracy}%</div>
                <div className="summary-accuracy-lbl">Accurate</div>
              </>
            )}
          </div>

          {/* Stat row */}
          <div className="summary-stat-row">
            <div className="summary-stat-card">
              <span className="summary-stat-num">{results.length}</span>
              <span className="summary-stat-lbl">Trials</span>
            </div>
            <div className="summary-stat-card summary-stat-correct">
              <span className="summary-stat-num">{correct}</span>
              <span className="summary-stat-lbl">Correct</span>
            </div>
            <div className="summary-stat-card summary-stat-incorrect">
              <span className="summary-stat-num">{incorrect}</span>
              <span className="summary-stat-lbl">Incorrect</span>
            </div>
          </div>

          {/* Missed / Correct breakdown */}
          {(topMissed.length > 0 || topCorrect.length > 0) && (
            <div className="summary-breakdown-row">
              {topMissed.length > 0 && (
                <div className="summary-breakdown-card summary-breakdown-missed">
                  <div className="summary-breakdown-title">Commonly Missed</div>
                  {topMissed.map(item => (
                    <div key={item.label} className="summary-breakdown-item">
                      <span className="summary-breakdown-label">{tc(item.label)}</span>
                      <span className="summary-breakdown-count summary-breakdown-count--miss">{item.wrong}✗</span>
                    </div>
                  ))}
                </div>
              )}
              {topCorrect.length > 0 && (
                <div className="summary-breakdown-card summary-breakdown-correct">
                  <div className="summary-breakdown-title">Strongest</div>
                  {topCorrect.map(item => (
                    <div key={item.label} className="summary-breakdown-item">
                      <span className="summary-breakdown-label">{tc(item.label)}</span>
                      <span className="summary-breakdown-count summary-breakdown-count--ok">{item.correct}✓</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Save session */}
          <div className="summary-save-card">
            <div className="summary-save-title">Save this session</div>
            <div className="summary-save-row">
              <input
                className="summary-save-input"
                type="text"
                value={sessionName}
                onChange={e => setSessionName(e.target.value)}
                placeholder="Session name…"
                disabled={saved}
              />
              <button
                className={`summary-save-btn${saved ? ' saved' : ''}`}
                onClick={handleSave}
                disabled={saving || saved}
              >
                {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          {/* Previous sessions */}
          {history.length > 0 && (
            <div className="summary-history-card">
              <div className="page-card-title">Previous Sessions</div>
              <hr className="page-card-divider" />
              {history.map(h => {
                const pct = h.total > 0 ? Math.round((h.correct / h.total) * 100) : 0;
                return (
                  <div key={h.id} className="history-row">
                    <span className="history-date">{h.sessionName || formatDate(h.completedAt)}</span>
                    <span className="history-accuracy">{pct}%</span>
                    <span className="history-detail">{h.correct}/{h.total}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Inline Run Again — visible at scroll bottom */}
          <button className="summary-run-again-btn" onClick={onRunAgain} style={{ marginTop: 8 }}>
            Run Again
          </button>

        </div>
      </div>

      {/* Fixed action bar — always visible regardless of scroll position */}
      <div className="summary-action-bar">
        <button className="summary-run-again-btn" onClick={onRunAgain}>Run Again</button>
        <button className="summary-back-btn" onClick={onBack}>Back to Decks</button>
      </div>
    </div>
  );
};

// ── Main Export ───────────────────────────────────────────────────────────────

export const SessionPage = () => {
  const { deckName: rawDeckName } = useParams();
  const deckName = rawDeckName ? decodeURIComponent(rawDeckName) : '';
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const uid = currentUser?.uid ?? null;

  // Mix Decks: when navigated from dashboard with multiple decks selected
  const mixDeckNames = location.state?.mixDeckNames;
  const mixedDeck = (mixDeckNames?.length >= 2)
    ? {
        name: mixDeckNames.join(' + '),
        emoji: '🔀',
        color: '#6B4A8B',
        items: DECKS.filter(d => mixDeckNames.includes(d.name)).flatMap(d => d.items),
        isMix: true,
      }
    : null;

  const searchParams = new URLSearchParams(location.search);
  const customDeckId = searchParams.get('deckId');

  // Static fallback: built-in deck (only used if no custom deck is found)
  const builtinDeck = mixedDeck || DECKS.find((d) => d.name === deckName) || null;

  const [customDeck, setCustomDeck] = useState(null);
  // Always show loading until Firestore check completes, so we never flash a built-in deck
  const [deckLoading, setDeckLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    if (!uid) {
      setDeckLoading(false);
      return;
    }
    const fetch = customDeckId
      ? getCustomDeckById(customDeckId)
      : getCustomDeckForSession(uid, deckName);
    fetch.then(result => {
      if (!mounted) return;
      if (result.success) setCustomDeck(result.deck);
      setDeckLoading(false);
    });
    return () => { mounted = false; };
  }, [uid, deckName, customDeckId]);

  // Custom deck always wins over built-in when both exist
  const deck = customDeck || builtinDeck;

  const [phase, setPhase] = useState('setup');
  const [sessionConfig, setSessionConfig] = useState(null);
  const [results, setResults] = useState([]);
  const [sessionKey, setSessionKey] = useState(0);
  const [sessionData, setSessionData] = useState(null);

  if (deckLoading) return <LoadingSpinner />;

  if (!deck) {
    return (
      <div className="session-page">
        <SessionHeaderBar onBack={() => navigate('/dashboard')} />
        <div className="session-body">
          <div className="setup-card" style={{ textAlign: 'center' }}>
            <p style={{ color: '#8A8A8A', marginBottom: 20 }}>Deck not found.</p>
            <button className="start-btn" onClick={() => navigate('/dashboard')}>Go Back</button>
          </div>
        </div>
      </div>
    );
  }

  const handleStart = (config) => {
    setSessionConfig(config);
    setResults([]);
    setPhase('trial');
  };

  const handleComplete = (finalResults) => {
    setResults(finalResults);
    setPhase('summary');

    if (uid && finalResults.length > 0) {
      const correct   = finalResults.filter((r) => r.correct === true).length;
      const incorrect = finalResults.filter((r) => r.correct === false).length;
      const accuracy  = Math.round((correct / finalResults.length) * 100);
      const cardResults = finalResults.map(r => ({ label: r.label || '', correct: r.correct }));

      // Auto-save immediately so data is never lost (use deck?.name for clean display name)
      const autoSavedName = `Session – ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
      saveSessionResult(uid, deck?.name || deckName, {
        correct, total: finalResults.length, accuracy, cardResults,
        sessionName: autoSavedName,
        deckEmoji: deck?.emoji || '',
      }).catch(() => {});

      setSessionData({ correct, total: finalResults.length, accuracy, cardResults, autoSaved: true, autoSavedName });

      const profileId = localStorage.getItem('sc-active-profile-id');
      if (profileId) {
        saveProfileSession(uid, profileId, {
          deckName: deck?.name || deckName,
          trials: finalResults.length,
          correct,
          incorrect,
          accuracy,
        }).catch(() => {});
      }
    }
  };

  // Run Again: keep the same sessionConfig, increment key to force TrialPhase remount
  const handleRunAgain = () => {
    setSessionKey((k) => k + 1);
    setResults([]);
    setSessionData(null);
    setPhase('trial');
  };

  if (phase === 'setup') {
    return (
      <SetupPhase
        deck={deck}
        onStart={handleStart}
        onBack={() => navigate('/dashboard')}
      />
    );
  }

  if (phase === 'trial' && sessionConfig) {
    return (
      <TrialPhase
        key={sessionKey}
        deck={deck}
        trialCount={sessionConfig.trialCount}
        targetItems={sessionConfig.targetItems}
        settings={sessionConfig.settings}
        onComplete={handleComplete}
        onQuit={() => navigate('/dashboard')}
      />
    );
  }

  if (phase === 'summary') {
    return (
      <SummaryPhase
        results={results}
        deckName={deckName}
        deckEmoji={deck?.emoji}
        userId={uid}
        onRunAgain={handleRunAgain}
        onBack={() => navigate('/dashboard')}
        sessionData={sessionData}
      />
    );
  }

  return null;
};
